// userStore.js
import { defineStore } from 'pinia';
import api from "@/core/services/axios";

function normalizeFeedbackItem(item) {
    return {
        ...item,
        sender_full_name: item.sender_full_name || item.sender_username || '',
        sender_avatar_url: normalizeAvatarUrl(item.sender_avatar_url),
    };
}

function normalizeAvatarUrl(url) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
        return url;
    }
    return `/uploads_public/${url}`;
}

export const useUserStore = defineStore('userStore', {
    state: () => ({
        currentUser: {},   // залогиненный пользователь (шапка)
        viewedUser: {},    // просматриваемый чужой профиль
        isLoading: false,

        // Состояние отзывов
        feedbacks: [],
        feedbackMeta: {
            currentPage: 1,
            limit: 10,
            totalCount: 0,
            totalPages: 1,
        },
        feedbacksLoading: false,
        feedbacksLoadingMore: false,
    }),

    actions: {
        // Загрузка профиля пользователя
        async fetchById(id, { target = 'viewed' } = {}) {
            try {
                const url = `/user/${id}`;
                const response = await api.get(url);
                const data = response?.data || {};
                if (target === 'current') {
                    this.currentUser = data;
                } else {
                    this.viewedUser = data;
                }
                return data;
            } catch (err) {
                return err;
            }
        },

        // Получение списка отзывов пользователя
        async fetchUserFeedbacks(userId, page = 1, limit = 10, append = false) {
            if (append) {
                this.feedbacksLoadingMore = true;
            } else {
                this.feedbacksLoading = true;
            }

            try {
                const response = await api.get(`/user/${userId}/feedback`, {
                    params: { page, limit }
                });
                const data = response?.data;
                if (data) {
                    const newItems = (data.data || []).map(normalizeFeedbackItem);
                    if (append) {
                        this.feedbacks = [...this.feedbacks, ...newItems];
                    } else {
                        this.feedbacks = newItems;
                    }

                    this.feedbackMeta = {
                        currentPage: data.current_page || page,
                        limit: data.limit || limit,
                        totalCount: data.total_count || 0,
                        totalPages: data.total_pages || 1,
                    };
                }
                return data;
            } catch (err) {
                console.error('Error fetching feedbacks:', err);
                throw err;
            } finally {
                this.feedbacksLoading = false;
                this.feedbacksLoadingMore = false;
            }
        },

        // Подгрузка следующей страницы отзывов («Загрузить ещё»)
        async loadMoreUserFeedbacks(userId) {
            if (this.feedbackMeta.currentPage >= this.feedbackMeta.totalPages) return;
            const nextPage = this.feedbackMeta.currentPage + 1;
            return await this.fetchUserFeedbacks(userId, nextPage, this.feedbackMeta.limit, true);
        },

        // Получение количества отзывов и средней оценки пользователя
        async fetchFeedbackCounts(userId) {
            try {
                const response = await api.get(`/user/${userId}/feedback/counts`);
                return response?.data || null;
            } catch (err) {
                console.error('Error fetching feedback counts:', err);
                throw err;
            }
        },

        // Оставить отзыв пользователю
        async createFeedback(userId, text) {
            try {
                const response = await api.post(`/user/${userId}/feedback`, { text });
                return response?.data;
            } catch (err) {
                console.error('Error creating feedback:', err);
                throw err;
            }
        },

        // Ответить на отзыв (комментарий владельца профиля)
        async respondFeedback(feedbackId, comment) {
            try {
                const response = await api.post(`/user/feedback/${feedbackId}/comment`, { comment });
                return response?.data;
            } catch (err) {
                console.error('Error responding to feedback:', err);
                throw err;
            }
        },

        // Редактировать отзыв
        async editFeedback(feedbackId, text) {
            try {
                const response = await api.patch(`/user/feedback/${feedbackId}`, { text });
                return response?.data;
            } catch (err) {
                console.error('Error editing feedback:', err);
                throw err;
            }
        },

        // Редактировать комментарий/ответ к отзыву
        async editFeedbackComment(feedbackId, comment) {
            try {
                const response = await api.patch(`/user/feedback/${feedbackId}/comment`, { comment });
                return response?.data;
            } catch (err) {
                console.error('Error editing feedback comment:', err);
                throw err;
            }
        },

        // Удалить ответ/комментарий к отзыву
        async removeFeedbackComment(feedbackId) {
            try {
                await api.delete(`/user/feedback/${feedbackId}/comment`);
            } catch (err) {
                console.error('Error removing feedback comment:', err);
                throw err;
            }
        },

        // Удалить отзыв
        async deleteFeedback(feedbackId) {
            try {
                await api.delete(`/user/feedback/${feedbackId}`);
            } catch (err) {
                console.error('Error deleting feedback:', err);
                throw err;
            }
        },
    },

    getters: {
        getUserDisplayName: (state) => {
            const u = state.currentUser;
            if (!u || Object.keys(u).length === 0) return null;

            return u.firstName && u.lastName
                ? `${u.firstName} ${u.lastName}`
                : u.firstName || u.lastName || u.username || null;
        },

        getUserName: (state) => {
            const u = state.currentUser;
            if (!u || Object.keys(u).length === 0) return null;

            return u.username || null;
        },

        hasMoreFeedbacks: (state) => {
            return state.feedbackMeta.currentPage < state.feedbackMeta.totalPages;
        }
    }
});